import { useEffect, useState, type FormEvent } from 'react'
import axios from 'axios'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Banknote, Check, CreditCard, Landmark, Minus, PackageCheck, Plus, ShoppingBag, Trash2 } from 'lucide-react'
import PageHeader from '../components/PageHeader'
import { useAuth } from '../hooks/useAuth'
import { useCart } from '../contexts/cartStore'
import { formatPrice, type PaymentMethod, type ShopQuote } from '../types/Shop'

interface OrderResult {
  orderNumber?: string
  order?: { orderNumber: string }
  emailSent: boolean
}

const paymentChoices: Array<{ id: PaymentMethod; label: string; description: string; enabled: boolean; icon: typeof Banknote }> = [
  { id: 'CASH', label: 'Espèces', description: 'À régler lors du retrait au club', enabled: true, icon: Banknote },
  { id: 'CHECK', label: 'Chèque', description: 'À remettre au club', enabled: true, icon: Check },
  { id: 'BANK_TRANSFER', label: 'Virement', description: 'Le club vous transmettra les modalités', enabled: true, icon: Landmark },
  { id: 'CARD', label: 'Carte', description: 'Bientôt disponible avec Stripe', enabled: false, icon: CreditCard },
]

const CartPage = () => {
  const { user } = useAuth()
  const { items, setQuantity, removeItem, clearCart } = useCart()
  const [quote, setQuote] = useState<ShopQuote | null>(null)
  const [quoteLoading, setQuoteLoading] = useState(false)
  const [quoteError, setQuoteError] = useState('')
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('CASH')
  const [customer, setCustomer] = useState({ firstName: '', lastName: '', email: '' })
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<OrderResult | null>(null)

  useEffect(() => {
    if (items.length === 0) {
      setQuote(null)
      setQuoteError('')
      return
    }

    let active = true
    setQuoteLoading(true)
    axios.post<ShopQuote>('/api/shop/quote', { items })
      .then(({ data }) => {
        if (!active) return
        setQuote(data)
        setQuoteError('')
      })
      .catch((error) => {
        if (!active) return
        setQuote(null)
        setQuoteError(axios.isAxiosError(error) ? error.response?.data?.message || 'Impossible de recalculer le panier.' : 'Impossible de recalculer le panier.')
      })
      .finally(() => active && setQuoteLoading(false))

    return () => { active = false }
  }, [items, user?.id])

  const submitOrder = async (event: FormEvent) => {
    event.preventDefault()
    if (!quote || submitting || paymentMethod === 'CARD') return
    setSubmitting(true)
    try {
      const { data } = await axios.post<OrderResult>('/api/shop/orders', {
        items,
        fingerprint: quote.fingerprint,
        paymentMethod,
        ...(!user ? { customer } : {}),
      })
      clearCart()
      setResult(data)
      toast.success('Votre commande est enregistrée')
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409 && error.response.data?.details?.fingerprint) {
        setQuote(error.response.data.details as ShopQuote)
        toast.error('Le panier a changé. Vérifiez le nouveau total avant de recommander.')
      } else {
        toast.error(axios.isAxiosError(error) ? error.response?.data?.message || 'Impossible de créer la commande' : 'Impossible de créer la commande')
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (result) {
    const orderNumber = result.order?.orderNumber ?? result.orderNumber
    return (
      <div className="shop-success-panel">
        <span className="shop-success-icon" aria-hidden="true"><PackageCheck /></span>
        <p className="eyebrow">Commande confirmée</p>
        <h1>Merci, votre commande est enregistrée.</h1>
        <p>Référence <strong>{orderNumber}</strong>. Le club va maintenant préparer vos articles pour le retrait.</p>
        {!result.emailSent && <p className="shop-warning">L’e-mail n’a pas pu être envoyé. Conservez cette référence et contactez le club si nécessaire.</p>}
        {!user && result.emailSent && <p>Un lien personnel vers toutes vos commandes vient de vous être envoyé par e-mail.</p>}
        <div className="flex flex-wrap justify-center gap-3">
          {user && <Link className="btn-primary" to="/mes-commandes">Voir mes commandes</Link>}
          <Link className="btn-secondary" to="/boutique">Retour à la boutique</Link>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Votre sélection" title="Panier" description="Vérifiez les tailles, les quantités et le tarif appliqué avant de valider votre commande." icon={<ShoppingBag />} />

      {items.length === 0 ? (
        <div className="empty-state">
          <ShoppingBag className="mx-auto mb-4 h-10 w-10 text-emerald-600" />
          <h2>Votre panier est vide</h2>
          <p className="mt-2 text-gray-600">Découvrez les goodies disponibles au club.</p>
          <Link to="/boutique" className="btn-primary mt-6">Voir la boutique</Link>
        </div>
      ) : (
        <form className="cart-layout" onSubmit={submitOrder}>
          <div className="space-y-5">
            <section className="cart-panel">
              <div className="cart-panel-heading"><div><p className="eyebrow !mb-1">Articles</p><h2>Votre commande</h2></div>{quoteLoading && <span className="shop-loading-chip">Mise à jour…</span>}</div>
              {quoteError && <div className="shop-error-box">{quoteError}</div>}
              <div className="cart-lines">
                {!quote && items.map((item) => (
                  <article className="cart-line" key={item.variantId}>
                    <div className="grid h-14 w-14 place-items-center rounded-2xl bg-emerald-50 text-emerald-700"><ShoppingBag /></div>
                    <div className="cart-line-copy"><strong>Article indisponible</strong><small>Référence de taille #{item.variantId} · Quantité {item.quantity}</small></div>
                    <span />
                    <span />
                    <button type="button" className="cart-remove" onClick={() => removeItem(item.variantId)} aria-label="Retirer cet article"><Trash2 /></button>
                  </article>
                ))}
                {(quote?.items ?? []).map((item) => (
                  <article className="cart-line" key={item.variantId}>
                    <img src={item.imageUrl} alt="" />
                    <div className="cart-line-copy">
                      <strong>{item.productName}</strong>
                      <small>Taille {item.variantLabel}</small>
                      {item.memberQuantity > 0 && <span className="member-price-note">1 unité au tarif adhérent</span>}
                    </div>
                    <div className="cart-quantity" aria-label={`Quantité de ${item.productName}`}>
                      <button type="button" onClick={() => setQuantity(item.variantId, item.quantity - 1)} aria-label="Diminuer la quantité"><Minus /></button>
                      <span>{item.quantity}</span>
                      <button type="button" onClick={() => setQuantity(item.variantId, item.quantity + 1)} disabled={item.quantity >= item.stock} aria-label="Augmenter la quantité"><Plus /></button>
                    </div>
                    <strong className="cart-line-total">{formatPrice(item.lineTotalCents)}</strong>
                    <button type="button" className="cart-remove" onClick={() => removeItem(item.variantId)} aria-label={`Retirer ${item.productName}`} title="Retirer"><Trash2 /></button>
                  </article>
                ))}
              </div>
            </section>

            {!user && (
              <section className="cart-panel">
                <div className="cart-panel-heading"><div><p className="eyebrow !mb-1">Acheteur public</p><h2>Vos coordonnées</h2></div></div>
                <p className="mb-5 text-sm text-gray-600">Le lien sécurisé vers votre historique sera envoyé uniquement à cette adresse.</p>
                <div className="shop-form-grid">
                  <label className="form-field" htmlFor="cart-first-name"><span className="form-label">Prénom</span><input id="cart-first-name" className="input-field" autoComplete="given-name" required minLength={2} placeholder="Votre prénom" value={customer.firstName} onChange={(event) => setCustomer({ ...customer, firstName: event.target.value })} /></label>
                  <label className="form-field" htmlFor="cart-last-name"><span className="form-label">Nom</span><input id="cart-last-name" className="input-field" autoComplete="family-name" required minLength={2} placeholder="Votre nom" value={customer.lastName} onChange={(event) => setCustomer({ ...customer, lastName: event.target.value })} /></label>
                  <label className="form-field sm:col-span-2" htmlFor="cart-email"><span className="form-label">E-mail</span><input id="cart-email" className="input-field" type="email" autoComplete="email" required placeholder="vous@exemple.fr" value={customer.email} onChange={(event) => setCustomer({ ...customer, email: event.target.value })} /></label>
                </div>
              </section>
            )}

            <section className="cart-panel">
              <div className="cart-panel-heading"><div><p className="eyebrow !mb-1">Règlement</p><h2>Moyen de paiement</h2></div></div>
              <div className="payment-choice-grid">
                {paymentChoices.map(({ id, label, description, enabled, icon: Icon }) => (
                  <label className={`payment-choice ${paymentMethod === id ? 'payment-choice-active' : ''} ${!enabled ? 'payment-choice-disabled' : ''}`} key={id}>
                    <input type="radio" name="paymentMethod" value={id} checked={paymentMethod === id} disabled={!enabled} onChange={() => setPaymentMethod(id)} />
                    <Icon aria-hidden="true" />
                    <span><strong>{label}</strong><small>{description}</small></span>
                  </label>
                ))}
              </div>
            </section>
          </div>

          <aside className="cart-summary">
            <p className="eyebrow !mb-2">Récapitulatif</p>
            <h2>Retrait au club</h2>
            <p>Votre commande sera réservée dès validation. Vous recevrez un message lorsqu’elle sera prête.</p>
            <div className="cart-summary-prices">
              {(quote?.items ?? []).map((item) => <span key={item.variantId}><small>{item.productName} × {item.quantity}</small><strong>{formatPrice(item.lineTotalCents)}</strong></span>)}
              <span className="cart-summary-total"><strong>Total</strong><strong>{quote ? formatPrice(quote.totalCents) : '—'}</strong></span>
            </div>
            <button className="btn-primary w-full justify-center" type="submit" disabled={!quote || quoteLoading || submitting || Boolean(quoteError)}>
              {submitting ? 'Validation…' : 'Passer la commande'}
            </button>
            <small className="block text-center text-gray-500">Aucun paiement en ligne n’est effectué.</small>
          </aside>
        </form>
      )}
    </div>
  )
}

export default CartPage
